export type CoinSlug = string
export type CoinSymbol = string
export type CoinName = string
export type CoinId = number

export interface RSSFeed {
  url: string
  filters: string
  source?: string
}

export interface CoinResources {
  rss_feeds: RSSFeed[]
  website_urls: string[]
  whitepaper_urls: string[]
  whitepaper_urls_v2: {
    url: string
    is_mica: boolean
  }[]
  explorer_urls: string[]
  sourcecode_urls: string[]
  message_board_urls: string[]
  announcement_urls: string[]
  reddit_urls: string[]
  twitter_urls: string[]
  telegram_urls: string[]
  discord_urls: string[]
  sustainability_disclosure_urls: string[]
}

export interface CoinPlatform {
  name: CoinName
  slug: CoinSlug
  token_address: string
}

export interface CoinMeta extends CoinResources {
  id: CoinId
  name: CoinName
  symbol: CoinSymbol
  slug: CoinSlug
  source_slug: CoinSlug
  active: boolean
  trading_view_status: number
  trading_view_symbol: string
  icon?: string | null
  price_engine_enabled: boolean
  address?: string
  description: string
  localeDescription?: string
  meta_title: string
  platform?: CoinPlatform[]
  tags?: string[] | null
  is_defi_swap: boolean
  chat_urls: string[]
}

export interface CoinMarket {
  slug: CoinSlug
  app_tradable: boolean
  exchange_tradable: boolean
  defi_tradable: boolean
  prices: number[]
  circulating_supply: number
  total_supply: number
  max_supply: number | null
  btc_price: number
  btc_price_change_24h: number | null
  btc_marketcap: number
  btc_volume_24h: number
  usd_price: number
  usd_price_change_24h: number | null
  usd_marketcap: number
  usd_volume_24h: number
  usd_price_change_24h_abs: number | null
  token_dominance_rate: number | null
  rank: number
  update_time: number
  price_update_time: number
  isPriceAlive: boolean
  app_symbol: string
  token_id: number
  restricted_areas?: string[]
  visibility?: 'public' | 'internal'
}

export type CoinFromAll = Pick<
  CoinMeta,
  'id' | 'name' | 'symbol' | 'slug' | 'source_slug' | 'icon' | 'price_engine_enabled'
> & {
  source_id: CoinId
}

// Global coins extends CoinFromAll with additional fields like rank
export type GlobalCoin = CoinFromAll & Pick<CoinMarket, 'rank'>

/**
 * CoinOverall extends CoinMeta and CoinMarket to provide a comprehensive
 * interface that combines both metadata (from CoinMeta) and market data (from CoinMarket).
 */
export interface CoinOverall extends CoinMeta, CoinMarket {}

export interface CryptoCurrency extends CoinOverall {
  community: any | null
  btc_price_update_time: number
  usd_price_update_time: number
  restricted_areas: any[]
  source_code: any | null
  page_title: string | null
  quote_source: string
}

export interface TopMoverItem {
  name: CoinName
  prices: number[]
  slug: CoinSlug
  symbol: CoinSymbol
  token_id: CoinId
  usd_price: number
  usd_price_change_24h: number
}

export interface TrendingCoin {
  slug: CoinSlug
  symbol: CoinSymbol
  name: CoinName
  token_id: CoinId
  usd_price_change_24h: number
}

export enum VisitType {
  Token = 1,
}

export interface PopularCoin {
  appTradeable: boolean
  exchangeTradable: boolean
  key: CoinId
  name: CoinName
  slug: CoinSlug
  symbol: CoinSymbol
  usdPriceChange24h: number
}

export enum PriceIndex {
  timestamp = 0,
  price = 1,
  marketCap = 2,
}

export interface CoinPrices {
  price_change: number
  prices: [number, number, number][] // [timestamp, price, market cap]
  usd_price_change: number
}

export interface Statistics {
  high: number
  low: number
  period: string
}

export interface CoinStatistics {
  statistics: Statistics[]
}

export type LiveCoinMarket = Partial<
  Pick<
    CoinOverall,
    'usd_price' | 'usd_marketcap' | 'usd_price_change_24h' | 'usd_volume_24h' | 'symbol' | 'slug'
  >
>

export interface CoinsAi {
  id: string
  slug: string
  latestNewsPage: {
    tldr: {
      summary?: string | null
      summaryLexical: SummaryLexical
    }
    latestNews: {
      summary?: string | null
      summaryLexical: SummaryLexical
    }
    socialMediaActivity: {
      summary?: string | null
      summaryLexical: SummaryLexical
    }
  }
  whatIsPage: {
    tldr: {
      summary?: string | null
      summaryLexical: SummaryLexical
    }
    coinHistory: {
      summary?: string | null
      summaryLexical: SummaryLexical
    }
    characteristicsAndTokenomics: {
      summary?: string | null
      summaryLexical: SummaryLexical
    }
    selectedLangs?:
      | (
          | 'ar'
          | 'hr'
          | 'da'
          | 'nl'
          | 'en-us'
          | 'fr'
          | 'fr-CA'
          | 'de'
          | 'el'
          | 'id'
          | 'it'
          | 'ko'
          | 'no'
          | 'pl'
          | 'pt'
          | 'pt-br'
          | 'ro'
          | 'ru'
          | 'es'
          | 'sv'
          | 'zh-TW'
          | 'vi'
        )[]
      | null
    tokenImage: string | Media
    symbol: string
    tokenId: string
    tokenName: string
    publishedAt: string
    publishedAt_tz: SupportedTimezones
    publishedBy: string
    translatedLocales?:
      | (
          | 'ar'
          | 'hr'
          | 'da'
          | 'nl'
          | 'en'
          | 'en-AR'
          | 'en-AU'
          | 'en-BR'
          | 'en-CA'
          | 'en-SG'
          | 'en-GB'
          | 'en-EEA'
          | 'en-US'
          | 'en-KR'
          | 'fr'
          | 'fr-CA'
          | 'fr-fr'
          | 'de'
          | 'el'
          | 'id'
          | 'it'
          | 'ko'
          | 'no'
          | 'pl'
          | 'pt'
          | 'pt-BR'
          | 'ro'
          | 'ru'
          | 'es'
          | 'es-es'
          | 'sv'
          | 'zh-TW'
          | 'tr'
          | 'vi'
          | 'es-US'
        )[]
      | null
    crowdinFileEtag?:
      | {
          [k: string]: unknown
        }
      | unknown[]
      | string
      | number
      | boolean
      | null
    crowdinFileID?: number | null
    crowdinHTMLContentFileID?: number | null
    lastUpdatedByLocale?:
      | {
          [k: string]: unknown
        }
      | unknown[]
      | string
      | number
      | boolean
      | null
    updatedAt: string
    createdAt: string
    _status?: ('draft' | 'published') | null
  }
}

export interface SummaryLexical {
  root: {
    type: string
    children: {
      type: any
      version: number
      [k: string]: unknown
    }[]
    direction: ('ltr' | 'rtl') | null
    format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | ''
    indent: number
    version: number
  }
  [k: string]: unknown
}

export type SupportedTimezones = 'Africa/Abidjan' | 'Asia/Hong_Kong' | 'Asia/Seoul'

export interface Media {
  id: string
  alt: string
  publishedBy: string
  folder?: (string | null) | FolderInterface
  updatedAt: string
  createdAt: string
  url?: string | null
  thumbnailURL?: string | null
  filename?: string | null
  mimeType?: string | null
  filesize?: number | null
  width?: number | null
  height?: number | null
  focalX?: number | null
  focalY?: number | null
}

export interface FolderInterface {
  id: string
  name: string
  folder?: (string | null) | FolderInterface
  documentsAndFolders?: {
    docs?: (
      | {
          relationTo?: 'payload-media-folders'
          value: string | FolderInterface
        }
      | {
          relationTo?: 'media'
          value: string | Media
        }
    )[]
    hasNextPage?: boolean
    totalDocs?: number
  }
  folderType?: 'media'[] | null
  updatedAt: string
  createdAt: string
}
